/**
 * CardTemplateEditor.jsx
 *
 * Editor UI for ProductCard template configuration.
 *
 * Renders:
 *   - Preset / Custom mode toggle
 *   - Preset selector (standard | horizontal | simple) when in preset mode
 *   - HTML template textarea when in custom mode
 *   - Inline validation hint when customCardTemplate is empty in custom mode
 *
 * Does NOT:
 *   - implement Mustache compilation (that is runtime-only)
 *   - expose arbitrary JS execution surfaces
 *   - duplicate template engine logic from kea-static-resources
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  Typography,
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup, Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { CARD_TEMPLATE_MODES, CARD_PRESETS } from './useCardTemplateSettings';
import IntlMessages from 'util/IntlMessages';
import { useLocalizedMessage } from 'util/useLocalizedMessage';

const useStyles = makeStyles((theme) => ({
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  modeToggle: {
    marginBottom: theme.spacing(2),
    padding: 4,
    border: '1px solid #ddeae5',
    borderRadius: 8,
    background: '#f5faf8',
  },
  modeButton: {
    minHeight: 36,
    border: '0 !important',
    borderRadius: '6px !important',
    color: '#66708a',
    textTransform: 'none',
    padding: theme.spacing(0.75, 2.5),
    '&.Mui-selected': {
      background: '#fff !important',
      boxShadow: '0 1px 3px rgba(30, 34, 72, 0.08)',
      color: '#226b51 !important',
    },
  },
  presetGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: theme.spacing(1),
  },
  densityGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 180px))',
    gap: theme.spacing(2),
  },
  templateTextarea: {
    width: '100%',
    fontFamily: '"Roboto Mono", "Courier New", monospace',
    fontSize: 13,
    lineHeight: 1.6,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: theme.spacing(1.5),
    resize: 'vertical',
    minHeight: 280,
    boxSizing: 'border-box',
    outline: 'none',
    '&:focus': {
      borderColor: theme.palette.primary.main,
    },
  },
  charCount: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    marginTop: theme.spacing(0.5),
    textAlign: 'right',
  },
  hintBox: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    background: '#f5f5f5',
    borderRadius: 4,
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  presetOption: {
    minWidth: 0,
    minHeight: 88,
    padding: theme.spacing(1.5),
    border: '1px solid #ddeae5',
    borderRadius: 8,
    background: '#fff',
    color: '#1e2248',
    cursor: 'pointer',
    font: 'inherit',
    textAlign: 'left',
    transition: 'border-color 150ms ease-out, background-color 150ms ease-out, box-shadow 150ms ease-out',
    '&:hover:not(:disabled)': {
      borderColor: 'var(--kea-color-primary-border)',
      background: 'var(--kea-color-primary-surface)',
    },
    '&:focus-visible': {
      outline: 'none',
      borderColor: 'var(--kea-color-primary)',
      boxShadow: '0 0 0 3px rgba(var(--kea-color-primary-rgb), 0.16)',
    },
    '&:disabled': {
      cursor: 'default',
      opacity: 0.6,
    },
  },
  presetOptionSelected: {
    borderColor: 'var(--kea-color-primary)',
    background: 'var(--kea-color-primary-soft)',
    boxShadow: '0 0 0 1px rgba(var(--kea-color-primary-rgb), 0.08)',
  },
  presetName: {
    display: 'block',
    marginBottom: theme.spacing(0.5),
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.35,
  },
  presetDescription: {
    display: 'block',
    color: '#66708a',
    fontSize: 12,
    lineHeight: 1.45,
  },
  '@media (max-width: 720px)': {
    presetGrid: {
      gridTemplateColumns: '1fr',
    },
    densityGrid: {
      gridTemplateColumns: '1fr',
    },
  },
  '@media (prefers-reduced-motion: reduce)': {
    presetOption: {
      transition: 'none',
    },
  },
}));

const PRESET_LABELS = {
  standard: 'ui.search.cardTemplate.preset.standard',
  horizontal: 'ui.search.cardTemplate.preset.horizontal',
  simple: 'ui.search.cardTemplate.preset.simple',
};

const PRESET_DESCRIPTIONS = {
  standard: 'ui.search.cardTemplate.preset.standard.description',
  horizontal: 'ui.search.cardTemplate.preset.horizontal.description',
  simple: 'ui.search.cardTemplate.preset.simple.description',
};

/**
 * CardTemplateEditor
 *
 * @param {{
 *   settings: { cardTemplateMode, cardType, customCardTemplate, cardPresentation },
 *   onChange: (patch: Object) => void,
 *   disabled: boolean,
 * }} props
 */
const CardTemplateEditor = ({ settings, onChange, disabled }) => {
  const classes = useStyles();
  const t = useLocalizedMessage();
  const {
    cardTemplateMode, cardType, customCardTemplate, cardPresentation,
  } = settings;
  const isCustom = cardTemplateMode === CARD_TEMPLATE_MODES.CUSTOM;
  const templateValue = customCardTemplate || '';

  const handleModeChange = (_event, newMode) => {
    if (!newMode) return; // ToggleButtonGroup requires at least one selected
    onChange({ cardTemplateMode: newMode });
  };

  const handleTemplateChange = (event) => {
    onChange({ customCardTemplate: event.target.value });
  };

  const handlePresentationChange = (event) => {
    const value = event.target.value.trim();
    if (!value) {
      onChange({ cardPresentation: null });
      return;
    }
    try {
      onChange({ cardPresentation: JSON.parse(value) });
    } catch (_error) {
      // Keep the last valid snapshot. The automation writes this bounded value
      // atomically; malformed/manual partial JSON must never enter the draft.
    }
  };

  const handleProductsInRowChange = (key) => (event) => {
    const value = Number(event.target.value);
    if (!Number.isInteger(value) || value < 1 || value > 6) return;
    onChange({ [key]: value });
  };

  const isEmptyCustom = isCustom && templateValue.trim() === '';

  return (
    <Box>
      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>
          <IntlMessages id="recommendations.settings.general.layout.title" />
        </Typography>
        <div className={classes.densityGrid} data-testid="card-grid-density">
          <TextField
            type="number"
            variant="outlined"
            size="small"
            label={t('ui.embedded.layout.configStepLayout.deviceToolbar.desktop')}
            value={settings.desktopProductsInRow}
            onChange={handleProductsInRowChange('desktopProductsInRow')}
            inputProps={{ min: 1, max: 6, step: 1, 'data-testid': 'products-per-row-desktop' }}
            disabled={disabled}
          />
          <TextField
            type="number"
            variant="outlined"
            size="small"
            label={t('ui.embedded.layout.configStepLayout.deviceToolbar.mobile')}
            value={settings.mobileProductsInRow}
            onChange={handleProductsInRowChange('mobileProductsInRow')}
            inputProps={{ min: 1, max: 6, step: 1, 'data-testid': 'products-per-row-mobile' }}
            disabled={disabled}
          />
        </div>
      </Box>

      {/* ── Mode Toggle ──────────────────────────────────────────────── */}
      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}><IntlMessages id="ui.search.customization.cardTemplate.cardTemplateEditor.templateMode" /></Typography>
        <ToggleButtonGroup
          value={cardTemplateMode}
          exclusive
          onChange={handleModeChange}
          size="small"
          className={classes.modeToggle}
          aria-label={t('ui.search.cardTemplate.mode.ariaLabel')}
          disabled={disabled}
        >
          <ToggleButton
            value={CARD_TEMPLATE_MODES.PRESET}
            className={classes.modeButton}
            aria-label={t('ui.search.cardTemplate.mode.preset.ariaLabel')}
            data-testid="card-template-mode-preset"
          >
            <IntlMessages id="ui.search.customization.cardTemplate.cardTemplateEditor.preset" />
          </ToggleButton>
          <ToggleButton
            value={CARD_TEMPLATE_MODES.CUSTOM}
            className={classes.modeButton}
            aria-label={t('ui.search.cardTemplate.mode.custom.ariaLabel')}
            data-testid="card-template-mode-custom"
          >
            <IntlMessages id="ui.search.customization.cardTemplate.cardTemplateEditor.customHtml" />
          </ToggleButton>
        </ToggleButtonGroup>

        {!isCustom && (
          <Typography variant="body2" color="textSecondary">
            <IntlMessages id="ui.search.customization.cardTemplate.cardTemplateEditor.chooseFromBuiltInCardLayouts" />
          </Typography>
        )}
        {isCustom && (
          <Typography variant="body2" color="textSecondary">
            <IntlMessages id="ui.search.customization.cardTemplate.cardTemplateEditor.writeAMustacheCompatibleHtmlTemplateUsingTheViewModelFields" />
          </Typography>
        )}
      </Box>

      {/* ── Preset Selector ─────────────────────────────────────────── */}
      {!isCustom && (
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}><IntlMessages id="ui.search.customization.cardTemplate.cardTemplateEditor.cardPreset" /></Typography>
          <div className={classes.presetGrid} data-testid="card-preset-options">
            {CARD_PRESETS.map((preset) => (
              <button
                type="button"
                key={preset}
                className={`${classes.presetOption} ${cardType === preset ? classes.presetOptionSelected : ''}`.trim()}
                onClick={() => onChange({ cardType: preset })}
                disabled={disabled}
                aria-pressed={cardType === preset}
                data-testid={`card-preset-${preset}`}
              >
                <span className={classes.presetName}>{t(PRESET_LABELS[preset])}</span>
                <span className={classes.presetDescription}>{t(PRESET_DESCRIPTIONS[preset])}</span>
              </button>
            ))}
          </div>
        </Box>
      )}

      {/* ── Custom Template Editor ───────────────────────────────────── */}
      {isCustom && (
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}><IntlMessages id="ui.search.customization.cardTemplate.cardTemplateEditor.customTemplateSource" /></Typography>

          {isEmptyCustom && (
            <Box mb={1}>
              <Alert severity="warning" data-testid="empty-template-warning">
                <IntlMessages id="ui.search.customization.cardTemplate.cardTemplateEditor.templateIsEmptyEnterValidMustacheHtmlBeforePublishingTo" />
              </Alert>
            </Box>
          )}

          <textarea
            className={classes.templateTextarea}
            value={templateValue}
            onChange={handleTemplateChange}
            placeholder={'<div class="kea-card">\n  <a data-kea-action="product.open" href="{{ product.url }}" data-kea-role="product-link">\n    {{#product.has_image}}\n    <img src="{{ product.image_url }}" alt="{{ product.title }}" loading="lazy" />\n    {{/product.has_image}}\n    <p class="kea-card__title">{{ product.title }}</p>\n    <span class="kea-card__price">{{ product.price_text }}</span>\n  </a>\n  {{#product.can_add_to_cart}}\n  <button data-kea-action="product.add_to_cart">{{ labels.add_to_cart }}</button>\n  {{/product.can_add_to_cart}}\n</div>'}
            disabled={disabled}
            aria-label={t('ui.search.cardTemplate.source.ariaLabel')}
            data-testid="custom-template-textarea"
            spellCheck={false}
          />

          <Typography className={classes.charCount}>
            {templateValue.length > 0
              ? t('ui.search.cardTemplate.source.characterCount', { count: templateValue.length })
              : t('ui.search.cardTemplate.source.empty')}
          </Typography>

          {/* Automation-owned store-analysis input. It deliberately has no raw
              JSON editor: operators should not hand-pick theme values. */}
          <textarea
            hidden
            value={cardPresentation ? JSON.stringify(cardPresentation) : ''}
            onChange={handlePresentationChange}
            disabled={disabled}
            data-testid="card-presentation-json"
          />

          <Box className={classes.hintBox}>
            <Typography variant="body2" component="span" style={{ fontWeight: 600 }}>
              <IntlMessages id="ui.search.customization.cardTemplate.cardTemplateEditor.howToTest" />
            </Typography>
            {' '}
            <Typography variant="body2" component="span">
              <IntlMessages id="ui.search.customization.cardTemplate.cardTemplateEditor.saveToBuildAPreviewThenOpenTheStorefrontAnd" />
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

CardTemplateEditor.propTypes = {
  settings: PropTypes.shape({
    cardTemplateMode: PropTypes.oneOf(Object.values(CARD_TEMPLATE_MODES)).isRequired,
    cardType: PropTypes.oneOf(CARD_PRESETS).isRequired,
    customCardTemplate: PropTypes.string,
    cardPresentation: PropTypes.object,
    desktopProductsInRow: PropTypes.number.isRequired,
    mobileProductsInRow: PropTypes.number.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

CardTemplateEditor.defaultProps = {
  disabled: false,
};

export default CardTemplateEditor;
